import { mapCurrencyFromDTO } from '@/modules/common/mappers/currency.mapper';
import {
  mapRateSourceFromDTO,
  mapRateSourceToDTO,
} from '@/modules/common/mappers/rate-source.mapper';
import { RateSourceDTO } from '@/modules/common/types/dto/rate-source.dto';
import { RateSource } from '@/modules/common/types/rate-source';
import { ProjectTeamMember, ProjectTeamMemberType } from '@/modules/projects';

import { ProjectFormData } from '../types';
import { ProjectBillingResponse } from '../types/dto/project-billing.dto';
import { CreateProjectBillingChangeRequest } from '../types/dto/request/create-project-request';
import { ProjectBilling, ProjectBillingModel } from '../types/project';
import {
  mapProjectBillingModelFromDTO,
  mapProjectBillingModelToDTO,
} from './project-billing-model.mapper';

/**
 * Maps ProjectTeamMember to CreateUserRateOnProjectRequest DTO.
 */
export const mapTeamMemberToUserRateDTO = (member: ProjectTeamMember) => {
  let rate = 0;
  switch (member.activeRateSource) {
    case RateSource.UserExternalRate:
      rate = member.userExternalRateValue || 0;
      break;
    case RateSource.SeniorityBasedRate:
      rate = member.seniorityBasedRateValue || 0;
      break;
    case RateSource.RoleBasedRate:
      rate = member.roleBasedRateValue || 0;
      break;
    case RateSource.GlobalDefaultRate:
      rate = member.globalDefaultRateValue || 0;
      break;
    case RateSource.ProjectRateCard:
      rate = member.projectRateCardValue || 0;
      break;
    case RateSource.Custom:
      rate = member.customRateValue || 0;
      break;
    default:
      rate = 0;
  }

  return {
    userId: parseInt(member.id, 10),
    rateSource: mapRateSourceToDTO(member.activeRateSource),
    rate,
  };
};

/**
 * Maps project billing response DTO to domain model.
 * Converts rate sources, currency, and team member structures.
 */
export const mapProjectBillingFromResponse = (response: ProjectBillingResponse): ProjectBilling => {
  return {
    billingModel: response.billingModel
      ? mapProjectBillingModelFromDTO(response.billingModel)
      : null,
    defaultCurrency: mapCurrencyFromDTO(response.defaultCurrency),
    rateSource: mapRateSourceFromDTO(response.rateSource),
    flatRate: response.flatRate,
    budget: response.budget,
    paymentDetailsId: response.paymentDetailsId,
    userRates: response.userRates.map(userRate => {
      const activeRateSource = mapRateSourceFromDTO(userRate.rateSource);

      return {
        id: String(userRate.user.id),
        name: userRate.user.name,
        email: userRate.user.email ?? undefined,
        avatarLink: userRate.user.profileImageUrl || undefined,
        type: userRate.hasAssignment
          ? ProjectTeamMemberType.HasAssignments
          : ProjectTeamMemberType.ManuallyAdded,
        activeRateSource,
        userExternalRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.USER)?.rate ?? 0,
        seniorityBasedRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.SENIORITY)?.rate ?? 0,
        roleBasedRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.ROLE)?.rate ?? 0,
        globalDefaultRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.ORGANIZATION)?.rate ?? 0,
        projectRateCardValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.RATE_CARD)?.rate ?? 0,
        // Custom rate: use top-level rate when active source is CUSTOM
        customRateValue: activeRateSource === RateSource.Custom ? userRate.rate : undefined,
      };
    }),
  };
};

/**
 * Maps ProjectFormData billing fields to CreateProjectBillingChangeRequest DTO.
 * Fixed Price billing model sends billingModel, currency, budget, and org default rateSource — no flat rate or user rates.
 */
export const mapProjectBillingToDTO = (
  data: ProjectFormData
): CreateProjectBillingChangeRequest => {
  const billingModel = mapProjectBillingModelToDTO(data.billingModel || ProjectBillingModel.TM);

  if (data.billingModel === ProjectBillingModel.FixedPrice) {
    return {
      billingModel,
      defaultCurrencyCode: data.currency,
      rateSource: mapRateSourceToDTO(data.defaultRateSource || RateSource.GlobalDefaultRate),
      budget: data.projectBudget,
      paymentDetailsId: data.paymentDetailsId ?? null,
    };
  }

  return {
    billingModel,
    defaultCurrencyCode: data.currency,
    rateSource: mapRateSourceToDTO(data.rateSource || RateSource.GlobalDefaultRate),
    flatRate: data.flatRate,
    budget: data.projectBudget,
    userRates: data.selectedTeamMembers?.map(mapTeamMemberToUserRateDTO),
    paymentDetailsId: data.paymentDetailsId ?? null,
  };
};

/**
 * Maps partial ProjectFormData billing fields to partial CreateProjectBillingChangeRequest DTO for updates.
 * Only includes fields that are defined (supports partial updates).
 * Fixed Price billing model omits rate source, flat rate, and user rates.
 */
export const mapPartialProjectBillingToDTO = (
  data: Partial<ProjectFormData>
): Partial<CreateProjectBillingChangeRequest> => {
  const billing: Partial<CreateProjectBillingChangeRequest> = {};

  if (data.billingModel !== undefined)
    billing.billingModel = mapProjectBillingModelToDTO(data.billingModel);
  if (data.currency !== undefined) billing.defaultCurrencyCode = data.currency;
  if (data.projectBudget !== undefined) billing.budget = data.projectBudget;
  if (data.paymentDetailsId !== undefined) billing.paymentDetailsId = data.paymentDetailsId ?? null;

  if (data.billingModel === ProjectBillingModel.FixedPrice) {
    // Fixed Price sends only the org default rate source — no flat rate or user rates
    billing.rateSource = mapRateSourceToDTO(data.defaultRateSource || RateSource.GlobalDefaultRate);
  } else {
    if (data.rateSource !== undefined) billing.rateSource = mapRateSourceToDTO(data.rateSource);
    if (data.flatRate !== undefined) billing.flatRate = data.flatRate;
    if (data.selectedTeamMembers !== undefined)
      billing.userRates = data.selectedTeamMembers.map(mapTeamMemberToUserRateDTO);
  }

  return billing;
};
